import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_OPERATOR_AIRPORT_DATA,
	URL_CMC_OPERATOR_CAPACITY_DATA,
	URL_CMC_OPERATOR_CONTACTS_DATA,
	URL_CMC_OPERATOR_IT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productPageProps } from "../../common/types/product";

export default function AircraftCharterData({
	packageName,
	subscribeLink,
	testimonials,
}: productPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Find charter aircraft available on a global scale with contact details to streamline your outreach."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Access detailed global charter aircraft data, including type, model, age, and connect with verified charter operators",
							"Get insights into seating capacity, range, cost estimates, distance, and flying time",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Aircraft Overview",
						description: (
							<>
								<p>
									Gain insights into charter aircraft
									availability worldwide by searching based on
									route and passenger capacity (number of
									premium and/or economy seats).
								</p>
								<p>Access detailed information, including:</p>
							</>
						),
						list: [
							"Route details (e.g., departure/arrival airports, great circle distance)",
							"Potential aircraft details (e.g., model, age, operator, seat configuration, stops, and range)",
							"Estimated costs",
							"Departure/arrival base distance",
							"Flight time",
							"Contact details (including the name and email of the person responsible for charter operations)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-charter-data/charter-search-by-airports.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for charter aircraft by route and seat capacity, using filters such as operator name, maximum stops, range, and more."
				slides={[
					{
						title: "Route",
						description: (
							<>
								<p>Search for charter aircraft by:</p>
								<ul className="ticks ticks--small">
									<li>Route (departure/arrival airport)</li>
									<li>
										Passenger capacity (number of
										economy/premium passenger capacity
									</li>
								</ul>
								<p>
									Apply restriction parameters while searching
									for charter aircraft:
								</p>
							</>
						),
						list: [
							"Operator name or code",
							"Maximum number of stops",
							"Range type (to define aircraft range capabilities)",
							"Oldest desired YOM (filter by earliest acceptable year of manufacture)",
							"IOSA certified (show only IOSA-approved operators)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-charter-data/charter-search-by-airports-economy.jpg",
							title: "List of IOSA-certified charter aircraft available on the JFK to Miami International route, configured with 10 economy seats",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Get access to more than 14,000 commercial operator and airport profiles, as well as start-up operators worldwide.",
						url: URL_CMC_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Operator Capacity Data",
						content:
							"Get access to detailed capacity, frequency, and market share information for operators and airports worldwide .",
						url: URL_CMC_OPERATOR_CAPACITY_DATA,
					},
					{
						title: "Operator Contacts Data",
						content:
							"Get a detailed contacts overview for almost any active as well as start-up operators worldwide.",
						url: URL_CMC_OPERATOR_CONTACTS_DATA,
					},
					{
						title: "Operator IT Data",
						content:
							"Get access to a Passenger Service System (PSS) providers for all scheduled airlines as well as each PSS's customer list.",
						url: URL_CMC_OPERATOR_IT_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology exportOptionsAvailable={false} />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Access detailed global charter aircraft data, including type, model, age, and connect with verified charter operators",
					"Get insights into seating capacity, range, cost estimates, distance, and flying time",
					"Regular updates to ensure up-to-date insights",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Commercial Aviation Aircraft Charter Data is not available for download."
				downloadsAvailable={false}
				dataFeedNotAvailableDisclaimer="Commercial Aviation Aircraft Charter Data is not available in a data feed form."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download these data sets?",
						content: (
							<p>
								Commercial Aviation Aircraft Charter Data is not
								available in a data feed form. To learn more
								about which data feeds are available for
								integration, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Commercial Aviation Aircraft Charter Data is not
								available for download. To learn more about
								similar data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "Can I contact charter operators directly?",
						content:
							"Yes. Within the Operator Contacts, Charter section, you’ll find contact details including operator names and dedicated charter email addresses to streamline your outreach.",
					},
				]}
			/>
		</>
	);
}
